import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import DOMPurify from 'isomorphic-dompurify';
import isNil from 'lodash-es/isNil';
import { useDrag, useDrop } from 'react-dnd';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import ActionPlaceholder from '@/components/organisms/Dialogs/Action/Placeholder/Placeholder';
import ActionHeader from '@/components/organisms/Dialogs/ActionHeader/ActionHeader';
import Box from '@/components/organisms/Dialogs/Box/Box';
import { MODAL_DELETE } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import useDeleteAction from '@/hooks/useDeleteAction';
import { cn } from '@/lib/utils';
import { selectHasError, selectHasWarning } from '@/redux/dialogAlerts/selectors';
import { TYPES } from '@/redux/dialogs/helper';
import { selectFirstActionIdByNodeId } from '@/redux/dialogs/selectors';
import { popModal, pushModal } from '@/redux/modals/actions';
import {
  addAction,
  moveAction,
  removeAction,
  selectCarousel,
  updateSelectedCarouselIndex,
} from '@/redux/nodes/actions';
import {
  selectIsActionSelected,
  selectIsIdInFlow,
  selectIsNodeConditionRequisiteSelected,
  selectSelectedCarouselIndex,
} from '@/redux/nodes/selectors';
import { CarouselAction } from '@/shared/types/action';
import { HANDLE_REGEX } from '@/util/markdown';
import { preventClickThrough } from '@/util/util';

import CarouselSlider from './CarouselSlider/CarouselSlider';
import { BoxErrorIcon, BoxWarningIcon } from '../Action/Action';

// `::before` pseudo-element scaffold: positions and sizes the connecting-arrow
// box above each card. Same shape as the shared `connecting-line` SCSS mixin
// used by Action.module.scss / Placeholder.module.scss.
const LINE_POSITIONING =
  'before:absolute before:bottom-full before:left-1/2 before:z-[1] before:h-[18px] before:w-2 before:-translate-x-1/2 before:bg-no-repeat before:content-[""]';

// Background image that fills the pseudo with the line SVG. Toggled
// independently so drop-placeholders keep the pseudo but hide the arrow.
export const LINE_SVG =
  "before:bg-[image:url('/connecting-line.svg')] [[data-art=gold]_&]:before:bg-[image:url('/assets/gold/connecting-line.svg')]";

interface CarouselProps {
  action: CarouselAction;
  actionId: string;
  nodeId: string;
  conditionIndex: number;
  requisiteIndex?: number;
  beforeActionId?: string;
  isAfterRequisite?: boolean;
  actionIndex?: number;
}

enum DIRECTION {
  PREVIOUS = 'previous',
  NEXT = 'next',
}

const Carousel = ({
  action,
  actionId,
  nodeId,
  conditionIndex,
  requisiteIndex = null,
  beforeActionId = null,
}: CarouselProps) => {
  const { type } = action;
  const dispatch = useDispatch();
  const selectedCarouselIndex = useSelector(selectSelectedCarouselIndex);
  const isSelected = useSelector(selectIsActionSelected(actionId));
  const [isDragging, setIsDragging] = useState(false);
  const { deleteAction } = useDeleteAction();
  const firstActionId = useSelector(selectFirstActionIdByNodeId(nodeId));
  const [activeCard, setActiveCard] = useState(selectedCarouselIndex ?? 0);
  const isActionInFlow = useSelector(selectIsIdInFlow(actionId));
  const isAnythingSelected = useSelector(selectIsNodeConditionRequisiteSelected);
  const hasDialogError = useSelector(selectHasError(actionId));
  const hasDialogWarning = useSelector(selectHasWarning(actionId));
  const { title, subtitle, buttons = [] } = action?.cards[activeCard] || {};

  // Highlight context variables in the subtitle
  const htmlSubtitle = useMemo(
    () =>
      DOMPurify.sanitize(
        subtitle?.replace(HANDLE_REGEX, '<span style="color: var(--primary);">$&</span>') ?? ''
      ),
    [subtitle]
  );

  const [_, dragRef] = useDrag({
    item: { actionId, nodeId, requisiteIndex, conditionIndex, move: true },
    type: TYPES.ACTION,
    canDrag: () => !document?.activeElement?.className?.match(/DraftEditor|input/),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const handleCarouselDelete = useCallback(() => {
    const deleteProps = {
      subtitle: <Trans i18nKey="dialog.delete_action" values={[type]} />,
      confirm: false,
      onDelete: () => {
        dispatch(removeAction({ actionId }));
        dispatch(popModal());
      },
    };
    dispatch(pushModal(MODAL_DELETE, deleteProps));
  }, [actionId, type, dispatch]);

  const onClick = useCallback(
    (e) => {
      preventClickThrough(e);
      if (isSelected) {
        return;
      }
      dispatch(selectCarousel({ actionId, index: activeCard ?? 0 }));
    },
    [isSelected, dispatch, actionId, activeCard]
  );

  const onKeyUp = useCallback(
    (e) => {
      preventClickThrough(e);
      if (e.key === 'Delete') {
        handleCarouselDelete();
      }
    },
    [handleCarouselDelete]
  );

  const navigateSlider = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    direction: DIRECTION
  ) => {
    preventClickThrough(e);
    dispatch(selectCarousel({ actionId }));

    if (direction === DIRECTION.PREVIOUS && action.cards[activeCard - 1]) {
      setActiveCard(activeCard - 1);
      dispatch(updateSelectedCarouselIndex({ index: activeCard - 1 }));
    }
    if (direction === DIRECTION.NEXT && action.cards[activeCard + 1]) {
      setActiveCard(activeCard + 1);
      dispatch(updateSelectedCarouselIndex({ index: activeCard + 1 }));
    }
  };

  const handlePaginationClick = (e, index: number) => {
    preventClickThrough(e);
    dispatch(selectCarousel({ actionId, index }));

    setActiveCard(index);
    dispatch(updateSelectedCarouselIndex({ index }));
  };

  const dropActionInNode = useCallback(
    (item) => {
      if (item.move) {
        return dispatch(
          moveAction({ source: item, target: { nodeId, conditionIndex, beforeActionId } })
        );
      }

      const newAction = { ...item.props, type: item.subType, action_id: item.id };

      return dispatch(addAction({ nodeId, conditionIndex, newAction }));
    },
    [nodeId, dispatch, conditionIndex, beforeActionId]
  );
  const [{ canDrop }] = useDrop({
    accept: !isNil(requisiteIndex) ? TYPES.QUESTION : TYPES.ACTION,
    drop: dropActionInNode,
    canDrop: () => {
      return true;
    },
    collect: (monitor) => ({ canDrop: monitor.canDrop() }),
  });

  useEffect(() => {
    if (isSelected) {
      setActiveCard(selectedCarouselIndex ?? 0);
    }
  }, [isSelected, selectedCarouselIndex]);

  return (
    <>
      <ActionPlaceholder
        nodeId={nodeId}
        beforeActionId={actionId}
        conditionIndex={conditionIndex}
      />

      <section
        id={actionId}
        className={cn(
          'relative w-60',
          // 13px = --space-16 (16px) − 3px, matches the shared SCSS wrapper.
          firstActionId === actionId ? 'mt-2.5' : 'mt-[13px]',
          LINE_POSITIONING,
          // Drop-placeholders keep the pseudo but hide the arrow.
          !canDrop && LINE_SVG,
          !isActionInFlow && !isAnythingSelected && 'opacity-50'
        )}
      >
        {hasDialogError && <BoxErrorIcon />}
        {!hasDialogError && hasDialogWarning && <BoxWarningIcon />}

        <Box
          className={cn(
            isSelected && 'border-border ring-1 ring-border',
            isSelected && isDragging && !hasDialogError && !hasDialogWarning && 'ring-inset',
            isSelected && hasDialogError && 'border-destructive/40 ring-destructive',
            isSelected && !hasDialogError && hasDialogWarning && 'border-warning ring-warning',
            !isActionInFlow && !isAnythingSelected && 'opacity-50'
          )}
          ref={(node) => {
            dragRef(node);
          }}
          role="button"
          tabIndex={0}
          onKeyUp={onKeyUp}
          onClick={onClick}
          onDragStart={() => setIsDragging(true)}
          onDragEnd={() => setIsDragging(false)}
          data-testid="carousel"
        >
          <ActionHeader
            type={type}
            objectToCopy={action}
            isSelected={isSelected}
            onDelete={() => deleteAction(action)}
            hasDialogError={hasDialogError}
          />

          <CarouselSlider
            activeCard={activeCard}
            cards={action?.cards}
            navigateSlider={navigateSlider}
            isDragging={isDragging}
            handlePaginationClick={handlePaginationClick}
          />

          <div className="w-full px-4 pt-3 pb-4 text-left">
            <p className="m-0 block truncate text-sm leading-5 font-normal">{title}</p>

            <p
              className="mt-1 mb-3 block truncate text-sm leading-5 font-normal text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: htmlSubtitle }}
            />

            {buttons.map(({ label }, index) => (
              <Button
                variant="secondary"
                className={cn(
                  'w-full [&_span]:block [&_span]:truncate',
                  index < buttons.length - 1 && 'mb-3'
                )}
                size="sm"
                // eslint-disable-next-line react/no-array-index-key
                key={`${label}-${index}`}
              >
                {label}
              </Button>
            ))}
          </div>
        </Box>
      </section>
    </>
  );
};

export default memo(Carousel);
